import { AxiosError, AxiosInstance } from 'axios'
import moment from 'moment'

import { createAuthedHttpClient } from '@driftt/web-auth'

import { DateRange } from 'components/DateRangePicker'
import config from 'config'
import {
  AccountActivity,
  AccountActivitySortValue,
  ContactWithActivites
} from 'modules/accountActivity/domain'
import { InsightTypeListType } from 'sections/CCRM/FeedCard'
import { transformFiltersToBackend } from 'utils/deserializeFilters'
import { ALL_ACTIVITY_TYPES } from 'utils/filters'
import { captureExceptionWithMessage } from 'utils/logRocket'
import { getTaskTypeFromSubject } from 'utils/salesforce'

import { getDefaultRange } from '../../components/DateRangePicker/index'

import {
  DeserializedSavedFilter,
  DeserializedSavedFilterOrgSettings,
  GlobalFilterSetting,
  INewSavedFilter,
  PagedActivityTimeline,
  PagedContactWithActivities,
  PagedResponse,
  SerializableFilterGroup,
  SerializableFilterGroupResponse
} from './types'

const logErrorHandler = (error: AxiosError) => {
  captureExceptionWithMessage('Account Activity API Error', error, {
    url: window.location.href,
    endpoint: error.config.url,
    errorData: JSON.stringify(error.config)
  })
}

const responseErrorHandlers = [logErrorHandler]

export const AccountActivityAPI: AxiosInstance = createAuthedHttpClient({
  responseErrorHandlers,
  baseConfig: {
    baseURL: config.ACCOUNT_ACTIVITY_API
  }
})
export default AccountActivityAPI

export const fetchAttributeOptions = async (attributeKey: string, searchValue?: string) => {
  return AccountActivityAPI.get('/v2/accounts/attribute_options', {
    params: { attributeKey, searchValue }
  })
}

export const fetchInsightById = async (insightId: string) => {
  return AccountActivityAPI.get(`/v2/insights/${encodeURIComponent(insightId)}`)
}

export const fetchPagedData = <T>({
  url,
  sortBy,
  descending,
  accountName,
  dateRange,
  accountId,
  contactId,
  filterGroups,
  insightTypes
}: {
  url: string
  sortBy?: AccountActivitySortValue
  descending?: boolean
  accountName?: string
  dateRange?: DateRange
  accountId?: string
  contactId?: string
  filterGroups: SerializableFilterGroup[]
  insightTypes?: InsightTypeListType
}) =>
  AccountActivityAPI.post<PagedResponse<T>>(url, {
    sortBy,
    descending,
    accountName,
    fromTime: dateRange?.startDate.valueOf(),
    toTime: dateRange?.endDate.valueOf(),
    accountId,
    contactId,
    filterGroups,
    ...(insightTypes && insightTypes.length > 0 && { insightTypes: insightTypes })
  }).then(res => res.data)

export const fetchAccountsCount = ({
  pageCursor = 0,
  pageSize = 10,
  sortBy,
  descending,
  accountName,
  dateRange = getDefaultRange(),
  accountId,
  contactId,
  insightTypes,
  filterGroups
}: {
  pageCursor?: number
  pageSize?: number
  sortBy?: AccountActivitySortValue
  descending?: boolean
  accountName?: string
  dateRange?: DateRange
  accountId?: string
  contactId?: string
  filterGroups: SerializableFilterGroup[]
  insightTypes?: string[]
}) =>
  AccountActivityAPI.post(`/v2/accounts/count/?pageSize=${pageSize}&pageCursor=${pageCursor}`, {
    sortBy,
    descending,
    accountName,
    fromTime: dateRange?.startDate.valueOf(),
    toTime: dateRange?.endDate.valueOf(),
    accountId,
    contactId,
    filterGroups,
    ...(insightTypes && insightTypes.length > 0 && { insightTypes: insightTypes })
  }).then(res => res.data)

export const fetchAccountContactCount = (accountId: string) =>
  AccountActivityAPI.get(`v2/accounts/${accountId}/contacts/timeline/count`, {})

export const fetchAccountContactActivity = ({
  accountId,
  sortBy,
  descending,
  limit,
  pageNum,
  fromTime
}: {
  accountId: string
  sortBy?: AccountActivitySortValue
  descending?: boolean
  limit: number
  pageNum: number
  fromTime?:number
}) =>
  AccountActivityAPI.post<PagedContactWithActivities>(
    `v2/accounts/${accountId}/contacts/timeline/paged?limit=${limit}&pageNum=${pageNum}`,
    {
      fromTime,
      sortBy,
      descending
    }
  ).then(res => res.data)

export const getAccountExplain = ({
  accountId,
  limit = 3
}: {
  accountId: string
  limit?: number
}) =>
  AccountActivityAPI.post<AccountActivity>(
    `v2/accounts/${accountId}/explain?topActivityCount=${limit}`,
    {
      activityTypes: [...ALL_ACTIVITY_TYPES]
    }
  ).then(res => res.data)

export const getSavedFilters = async (): Promise<SerializableFilterGroupResponse> => {
  const res = await AccountActivityAPI.get<SerializableFilterGroupResponse>(`v2/filter_sets`)
  return res.data
}

const serializeSavedFilter = (savedFilter: DeserializedSavedFilter | INewSavedFilter) => {
  return {
    ...savedFilter,
    body: {
      ...savedFilter.body,
      filterGroups: transformFiltersToBackend(savedFilter.body.filterGroups)
    }
  }
}

export const getSavedFilterSets = () =>
  AccountActivityAPI.get<{ savedFilterSets: DeserializedSavedFilter[] }>(`/v2/filter_sets`).then(
    res => res?.data?.savedFilterSets || []
  )

export const updateSavedFilterSets = (payload: DeserializedSavedFilter[]) =>
  AccountActivityAPI.post('/v2/filter_sets/subscription', payload)

export const createSavedFilter = (newSavedFilter: INewSavedFilter) => {
  const serializedSavedFilter = serializeSavedFilter(newSavedFilter)
  return AccountActivityAPI.post('/v2/filter_sets', serializedSavedFilter)
}

export const assignAdminDefaultOrgFilterSettings = (
  filterId: number,
  orgSettings: DeserializedSavedFilterOrgSettings
) => {
  return AccountActivityAPI.post(`/v2/filter_sets/default/org_settings/${filterId}`, orgSettings)
}

export const patchSavedFilter = (
  savedFilterId: number,
  updatedSavedFilter: DeserializedSavedFilter
) => {
  const serializedSavedFilter = serializeSavedFilter(updatedSavedFilter)
  return AccountActivityAPI.post(`/v2/filter_sets/${savedFilterId}`, serializedSavedFilter)
}

export const deleteSavedFilter = (savedFilterId: number) => {
  return AccountActivityAPI.delete(`/v2/filter_sets/${savedFilterId}`)
}

export const setDefaultFilter = (savedFilterId: number) => {
  return AccountActivityAPI.post(`/v2/filter_sets/default/${savedFilterId}`)
}

export const removeDefaultFilter = () => {
  return AccountActivityAPI.delete('/v2/filter_sets/default')
}

export const toggleDefaultFilter = (savedFilterId: number, isDefault: boolean) => {
  const deselectedParam = !isDefault ? '?deselected=true' : ''
  return AccountActivityAPI.post(`/v2/filter_sets/default/${savedFilterId}${deselectedParam}`)
}

export const fetchDeals = ({
  filters,
  accountId,
  search,
  dateRange,
  sortValue,
  sortDescending
}: {
  filters: Record<string, any>
  accountId?: string
  search?: string
  dateRange?: {
    from: number
    to: number
  }
  sortValue: AccountActivitySortValue
  sortDescending: boolean
}) => {
  return AccountActivityAPI.post('/deals', {
    sortBy: sortValue,
    descending: sortDescending,
    accountId,
    accountName: search,
    fromTime: dateRange?.from,
    toTime: dateRange?.to,
    filterGroups: [filters]
  }).then(res => res.data)
}

export const fetchDealTotals = ({
  filters,
  search,
  dateRange
}: {
  filters: Record<string, any>
  search: string | undefined
  dateRange:
  | {
    from: number
    to: number
  }
  | undefined
}) => {
  return AccountActivityAPI.post('/deals/totals', {
    accountName: search,
    fromTime: dateRange?.from,
    toTime: dateRange?.to,
    filterGroups: [filters]
  }).then(res => res.data)
}

const fetchSalesforceTasks = ({
  accountId,
  ...params
}: {
  accountId: string
  type?: string
  startDate?: string
  endDate?: string
}) => {
  let query: URLSearchParams | string = new URLSearchParams({ ...params })
  query = query ? `?${query}` : ''
  return AccountActivityAPI.get(`/tasks/sfdc/fetch/${accountId}${query}`).then(({ data }) => data)
}

export const fetchAccountEngagementSummary = async (
  accountId: string,
  firstDayOfMonth: number,
  lastDayOfMonth: number,
  withTasks?: boolean
) => {
  const driftEngagementSummary = AccountActivityAPI.get(
    `/v2/accounts/${accountId}/summary?startTime=${firstDayOfMonth}&endTime=${lastDayOfMonth}`
  ).then(res => res.data)

  if (withTasks) {
    try {
      const tasks = await fetchSalesforceTasks({
        accountId,
        startDate: moment(firstDayOfMonth).format('YYYY-MM-DD'),
        endDate: moment(lastDayOfMonth).format('YYYY-MM-DD')
      })

      const driftData = await driftEngagementSummary
      const combinedEngagementData = tasks.reduce((acc: any, currentVal: any) => {
        const type = getTaskTypeFromSubject(currentVal.Subject) || currentVal.Type
        const day = moment(currentVal.ActivityDate)
          .toDate()
          .getTime()
        const engagementByDay = acc.engagementByDay
        const dataByDate = engagementByDay.find(({ startOfDay }: any) => startOfDay === day) || {}
        let count = dataByDate?.engagementByType[type] || 0

        engagementByDay.indexOf(dataByDate) > -1 &&
          (engagementByDay[engagementByDay.indexOf(dataByDate)] = {
            ...dataByDate,
            engagementByType: {
              ...dataByDate.engagementByType,
              [type]: ++count
            }
          })

        return {
          ...acc,
          engagementByDay: engagementByDay
        }
      }, driftData)

      return Promise.resolve(combinedEngagementData)
    } catch (e) {
      return Promise.reject(e)
    }
  } else {
    return driftEngagementSummary
  }
}

export const fetchAccountAISummary = (accountId: string) =>
  AccountActivityAPI.post<string[]>(`v2/accounts/${accountId}/ai-summary`)

export const getGlobalFiltersSettings = () => {
  return AccountActivityAPI.get<GlobalFilterSetting[]>('v2/global_filters')
}

export const setGlobalFiltersSettings = (settings: GlobalFilterSetting[]) => {
  return AccountActivityAPI.post<GlobalFilterSetting[]>('v2/global_filters/batch', settings)
}

export const fetchMeetingInsights = (videoId: number) => {
  return AccountActivityAPI.get(`/conversation/insights/meeting/${videoId}`).then(res => res.data)
}

export const fetchMissedAccountsCSV = (orgId: number) => {
  return AccountActivityAPI.get(`/v2/activity_report/missed_accounts/org/${orgId}/export`).then(
    res => res.data
  )
}

export const getAccountTopVisitors = (accountId: string) => {
  return AccountActivityAPI.get<ContactWithActivites[]>(
    `v2/accounts/${accountId}/contacts/top?limit=5`
  )
}

export const getTimelineActivities = (
  startTime: number,
  limit: number,
  accountId?: string,
  endUserId?: string
) => {
  const searchParams = new URLSearchParams({
    startTime: `${startTime}`,
    limit: `${limit}`
  })
  accountId && searchParams.append('accountId', accountId)
  endUserId && searchParams.append('endUserId', endUserId)

  return AccountActivityAPI.get<PagedActivityTimeline>(
    `/activities/timeline/paged?${searchParams.toString()}`
  ).then(res => {
    return res.data;
  });
}
